import { $t } from "../../../i18n";
import type en from "../../../i18n/locales/en.json";
import type { IconName } from "../../icons/iconNames.generated";
import { FilterOperator, LogicalOperator } from "../filterModel";
import type { ColumnFilterConfig, ConditionFilterKind, ListColumnFilterConfig, OperatorOption } from "../types";

type OperatorLabelKey = `columnFilter.operator.${keyof (typeof en)["columnFilter"]["operator"]}`;

/** How many terms the editor asks for: none, one, or a range. */
export type OperatorArity = 0 | 1 | 2;

export interface OperatorSpec {
  labelKey: OperatorLabelKey;
  icon: IconName;
  arity: OperatorArity;
}

export const OPERATOR_SPECS = {
  [FilterOperator.CONTAINS]: { labelKey: "columnFilter.operator.contains", icon: "phosphor/brackets-square", arity: 1 },
  [FilterOperator.NOT_CONTAINS]: {
    labelKey: "columnFilter.operator.notContains",
    icon: "phosphor/text-t-slash",
    arity: 1,
  },
  [FilterOperator.EQUALS]: { labelKey: "columnFilter.operator.equals", icon: "phosphor/equals", arity: 1 },
  [FilterOperator.NOT_EQUALS]: { labelKey: "columnFilter.operator.notEquals", icon: "phosphor/not-equals", arity: 1 },
  [FilterOperator.GREATER_THAN]: {
    labelKey: "columnFilter.operator.greaterThan",
    icon: "phosphor/greater-than",
    arity: 1,
  },
  [FilterOperator.GREATER_OR_EQUALS]: {
    labelKey: "columnFilter.operator.greaterOrEquals",
    icon: "phosphor/greater-than-or-equal",
    arity: 1,
  },
  [FilterOperator.LESS_THAN]: { labelKey: "columnFilter.operator.lessThan", icon: "phosphor/less-than", arity: 1 },
  [FilterOperator.LESS_OR_EQUALS]: {
    labelKey: "columnFilter.operator.lessOrEquals",
    icon: "phosphor/less-than-or-equal",
    arity: 1,
  },
  [FilterOperator.STARTS_WITH]: {
    labelKey: "columnFilter.operator.startsWith",
    icon: "phosphor/arrow-bend-left-down",
    arity: 1,
  },
  [FilterOperator.ENDS_WITH]: {
    labelKey: "columnFilter.operator.endsWith",
    icon: "phosphor/arrow-bend-right-down",
    arity: 1,
  },
  [FilterOperator.BLANK]: { labelKey: "columnFilter.operator.blank", icon: "phosphor/circle-dashed", arity: 0 },
  [FilterOperator.NOT_BLANK]: { labelKey: "columnFilter.operator.notBlank", icon: "phosphor/circle", arity: 0 },
  [FilterOperator.IN_RANGE]: {
    labelKey: "columnFilter.operator.between",
    icon: "phosphor/arrows-horizontal",
    arity: 2,
  },
} as const satisfies Record<FilterOperator, OperatorSpec>;

export const OPERATOR_MENUS = {
  text: [
    FilterOperator.CONTAINS,
    FilterOperator.NOT_CONTAINS,
    FilterOperator.EQUALS,
    FilterOperator.NOT_EQUALS,
    FilterOperator.STARTS_WITH,
    FilterOperator.ENDS_WITH,
    FilterOperator.BLANK,
    FilterOperator.NOT_BLANK,
  ],
  number: [
    FilterOperator.EQUALS,
    FilterOperator.NOT_EQUALS,
    FilterOperator.GREATER_THAN,
    FilterOperator.GREATER_OR_EQUALS,
    FilterOperator.LESS_THAN,
    FilterOperator.LESS_OR_EQUALS,
    FilterOperator.IN_RANGE,
    FilterOperator.BLANK,
    FilterOperator.NOT_BLANK,
  ],
  date: [
    FilterOperator.EQUALS,
    FilterOperator.NOT_EQUALS,
    FilterOperator.LESS_THAN,
    FilterOperator.GREATER_THAN,
    FilterOperator.IN_RANGE,
  ],
} as const satisfies Record<ConditionFilterKind, readonly FilterOperator[]>;

/** Blank / Not blank in a list column's text conditions, unless it opts in: several list endpoints reject them. */
export function listConditionDisabledOperators(config: ListColumnFilterConfig): readonly FilterOperator[] {
  return config.blankConditions ? [] : VALUELESS_OPERATORS;
}

export const LIST_CONDITION_DEFAULT_OPERATOR = FilterOperator.CONTAINS;

/** Dates read "Before" / "After" where numbers read "Less than" / "Greater than". */
export const MENU_LABEL_OVERRIDES: Partial<
  Record<ConditionFilterKind, Partial<Record<FilterOperator, OperatorLabelKey>>>
> = {
  date: {
    [FilterOperator.LESS_THAN]: "columnFilter.operator.before",
    [FilterOperator.GREATER_THAN]: "columnFilter.operator.after",
  },
};

/** Editor-only operators: they are encoded as two conditions and never sent as such. */
export const RANGE_OPERATORS: readonly FilterOperator[] = [FilterOperator.IN_RANGE];

/** Operators that filter without a term: the editor applies them at once and sends `filterTerm: ""`. */
export const VALUELESS_OPERATORS: readonly FilterOperator[] = (Object.keys(OPERATOR_SPECS) as FilterOperator[]).filter(
  (operator) => OPERATOR_SPECS[operator].arity === 0,
);

export function isValuelessOperator(operator: FilterOperator | string | undefined): boolean {
  return VALUELESS_OPERATORS.includes(operator as FilterOperator);
}

/** The operator's name in the current locale; `kind` picks the menu wording (dates read "Before" / "After"). */
export function operatorLabel(operator: FilterOperator, kind?: ConditionFilterKind): string {
  return $t((kind && MENU_LABEL_OVERRIDES[kind]?.[operator]) ?? OPERATOR_SPECS[operator].labelKey);
}

export function joinLabel(join: LogicalOperator): string {
  return $t(join === LogicalOperator.OR ? "common.label.or" : "common.label.and");
}

/** The operators a user can select: neither excluded nor disabled. */
export function menuFor(config: ColumnFilterConfig): readonly FilterOperator[] {
  if (config.kind === "list") {
    if (!config.textConditions) return [];
    const disabledOperators = listConditionDisabledOperators(config);
    return OPERATOR_MENUS.text.filter((operator) => !disabledOperators.includes(operator));
  }
  if (config.kind === "date") return OPERATOR_MENUS.date;
  const disabledOperators = config.disabledOperators ?? [];
  return OPERATOR_MENUS[config.kind].filter(
    (operator) => !config.excludedOperators.includes(operator) && !disabledOperators.includes(operator),
  );
}

/** The menu of a text, number or date filter, labelled in the current locale. Disabled operators stay listed. */
export function operatorOptionsFor(
  kind: ConditionFilterKind,
  excludedOperators: readonly FilterOperator[] = [],
  disabledOperators: readonly FilterOperator[] = [],
): OperatorOption[] {
  return OPERATOR_MENUS[kind]
    .filter((value) => !excludedOperators.includes(value))
    .map((value) => ({
      value,
      label: operatorLabel(value, kind),
      icon: OPERATOR_SPECS[value].icon,
      disabled: disabledOperators.includes(value),
    }));
}
